import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Plus, Search } from 'lucide-react';
import { MenuStatutCours, PastilleStatutCours } from '../components/cours/StatutCours';
import { Chargement, MessageErreur } from '../components/ui/Primitives';
import { ErreurApi } from '../lib/api';
import { useValeurDifferee } from '../lib/useRecherche';
import { useMesCours, type LigneCours, type ListeCours, type StatutCours } from '../lib/useCours';

/**
 * Mes cours (écran `instructor-courses`, TF-443) : les cours que le formateur
 * a créés dans « Créer un cours », avec leur statut — brouillon, publié,
 * archivé. Recherche dans le titre, filtre par titre certifiant ; un clic
 * ouvre le cours en édition. La direction y voit les cours de tous les
 * formateurs. Les gestes de statut viennent de l'API, avec ce qui les bloque.
 */

type Onglet = StatutCours | '';

const ONGLETS: readonly { cle: Onglet; libelle: string; aucun: string }[] = [
  { cle: '', libelle: 'Tous', aucun: 'Aucun cours.' },
  { cle: 'BROUILLON', libelle: 'Brouillons', aucun: 'Aucun cours en brouillon.' },
  { cle: 'PUBLIE', libelle: 'Publiés', aucun: 'Aucun cours publié.' },
  { cle: 'ARCHIVE', libelle: 'Archivés', aucun: 'Aucun cours archivé.' },
];

// Dégradés des cartes de la maquette ; un même titre certifiant garde le sien.
const DEGRADES = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-green-600',
  'from-yellow-500 to-amber-600',
  'from-pink-500 to-fuchsia-600',
  'from-cyan-500 to-blue-600',
  'from-red-500 to-orange-600',
  'from-blue-600 to-blue-700',
  'from-slate-800 to-slate-900',
] as const;

function degradeDe(cours: LigneCours): string {
  if (cours.statut === 'ARCHIVE') return 'from-gray-400 to-gray-500';
  const somme = [...cours.formation.id].reduce((total, caractere) => total + caractere.charCodeAt(0), 0);
  return DEGRADES[somme % DEGRADES.length]!;
}

function pluriel(nombre: number, singulier: string, plurielForme: string): string {
  return `${nombre} ${nombre > 1 ? plurielForme : singulier}`;
}

function programmeDe(cours: LigneCours): string {
  if (cours.nombreModules === 0) return 'Programme vide';
  return `${pluriel(cours.nombreModules, 'module', 'modules')} · ${pluriel(cours.nombreLecons, 'leçon', 'leçons')}`;
}

function dateDe(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

function resume(liste: ListeCours): string {
  const { BROUILLON, PUBLIE, ARCHIVE } = liste.totaux;
  const morceaux = [
    pluriel(BROUILLON + PUBLIE + ARCHIVE, 'cours créé', 'cours créés'),
    pluriel(PUBLIE, 'publié', 'publiés'),
    pluriel(BROUILLON, 'brouillon', 'brouillons'),
  ];
  if (ARCHIVE > 0) morceaux.push(pluriel(ARCHIVE, 'archivé', 'archivés'));
  return morceaux.join(' · ');
}

function CarteCours({ cours, avecAuteur }: { cours: LigneCours; avecAuteur: boolean }): JSX.Element {
  return (
    <article className="relative rounded-2xl bg-white shadow-card transition focus-within:z-10 focus-within:ring-2 focus-within:ring-codialis-mint hover:-translate-y-1 hover:shadow-lg">
      <div className={`relative flex h-28 items-center justify-center rounded-t-2xl bg-gradient-to-br ${degradeDe(cours)}`}>
        <BookOpen className="h-10 w-10 text-white/80" aria-hidden />
        <div className="absolute left-3 top-3">
          <PastilleStatutCours statut={cours.statut} libelle={cours.statutLibelle} majuscules />
        </div>
        <div className="absolute right-3 top-3 z-10">
          <MenuStatutCours coursId={cours.id} titre={cours.titre} gestes={cours.gestes} />
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-display mb-2 line-clamp-2 min-h-[2.6rem] text-sm font-bold text-codialis-navy">
          <Link
            to={`/instructor-create?cours=${encodeURIComponent(cours.id)}`}
            className="after:absolute after:inset-0 after:rounded-2xl focus:outline-none"
          >
            {cours.titre}
          </Link>
        </h3>
        <div className="mb-3 text-xs text-codialis-gray">
          {programmeDe(cours)}
          {avecAuteur && ` · ${cours.auteur}`}
        </div>
        <div className="mb-3 truncate text-xs text-codialis-gray" title={cours.bloc?.libelle}>
          {cours.bloc ? `Bloc : ${cours.bloc.libelle}` : 'Cours transversal'}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3 text-xs text-codialis-gray">
          <span className="min-w-0 truncate" title={cours.formation.titre}>
            {cours.formation.titre}
          </span>
          <span className="shrink-0">Modifié le {dateDe(cours.modifieLe)}</span>
        </div>
      </div>
    </article>
  );
}

export function InstructorCoursesPage(): JSX.Element {
  const navigate = useNavigate();
  const [saisie, setSaisie] = useState('');
  const [formationId, setFormationId] = useState('');
  const [onglet, setOnglet] = useState<Onglet>('');
  const recherche = useValeurDifferee(saisie.trim(), 300);
  const liste = useMesCours({ recherche, formationId, statut: onglet });
  const donnees = liste.data;

  const filtre = Boolean(saisie.trim() || formationId);
  const total = donnees ? donnees.totaux.BROUILLON + donnees.totaux.PUBLIE + donnees.totaux.ARCHIVE : 0;
  const compte = (cle: Onglet): number => {
    if (!donnees) return 0;
    const { BROUILLON, PUBLIE, ARCHIVE } = donnees.compteurs;
    return cle === '' ? BROUILLON + PUBLIE + ARCHIVE : donnees.compteurs[cle];
  };
  const reinitialiser = () => {
    setSaisie('');
    setFormationId('');
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      {/* En-tête */}
      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-extrabold text-codialis-navy">
            {donnees?.perimetre === 'TOUS' ? 'Cours en ligne' : 'Mes cours'}
          </h1>
          <p className="mt-1 text-sm text-codialis-gray">
            {donnees
              ? `${donnees.perimetre === 'TOUS' ? 'Tous les formateurs · ' : ''}${resume(donnees)}`
              : 'Les cours créés dans « Créer un cours »'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/instructor-create')}
          className="flex min-h-[44px] items-center gap-2 rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
        >
          <Plus className="h-4 w-4" aria-hidden />
          Créer un nouveau cours
        </button>
      </div>

      {liste.error && (
        <MessageErreur
          message={liste.error instanceof ErreurApi ? liste.error.message : 'Chargement des cours impossible.'}
        />
      )}
      {liste.isLoading && <Chargement libelle="Chargement des cours…" />}

      {donnees && total === 0 && (
        <div className="rounded-2xl bg-white p-6 text-center shadow-card sm:p-10">
          <BookOpen className="mx-auto h-10 w-10 text-codialis-gray" aria-hidden />
          <p className="mt-3 text-sm text-codialis-gray">
            {donnees.perimetre === 'TOUS' ? 'Aucun cours n’a encore été créé.' : 'Vous n’avez encore créé aucun cours.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/instructor-create')}
            className="mt-4 min-h-[44px] rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
          >
            Créer un cours
          </button>
        </div>
      )}

      {donnees && total > 0 && (
        <>
          {/* Recherche et titre certifiant */}
          <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-card">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <label className="relative min-w-0 flex-1" htmlFor="recherche-cours">
                <span className="sr-only">Rechercher un cours par son titre</span>
                <Search
                  className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray"
                  aria-hidden="true"
                />
                <input
                  id="recherche-cours"
                  type="search"
                  value={saisie}
                  maxLength={150}
                  onChange={(evenement) => setSaisie(evenement.target.value)}
                  placeholder="Rechercher par titre…"
                  className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-10 pr-3.5 text-sm text-codialis-navy transition placeholder:text-gray-400 focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20 [@media(pointer:coarse)]:min-h-[44px]"
                />
              </label>
              <label className="min-w-0 md:w-80" htmlFor="filtre-formation">
                <span className="sr-only">Filtrer par titre certifiant</span>
                <select
                  id="filtre-formation"
                  value={formationId}
                  onChange={(evenement) => setFormationId(evenement.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20 [@media(pointer:coarse)]:min-h-[44px]"
                >
                  <option value="">Tous les titres certifiants</option>
                  {donnees.formations.map((formation) => (
                    <option key={formation.id} value={formation.id}>
                      {formation.titre}
                      {formation.rncp ? ` (RNCP ${formation.rncp})` : ''}
                      {formation.actif ? '' : ' · désactivée'}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* Onglets */}
          <div className="max-w-full overflow-x-auto">
            <div role="group" aria-label="Filtrer par statut" className="inline-flex gap-1 rounded-xl bg-white p-1 shadow-card">
              {ONGLETS.map(({ cle, libelle }) => (
                <button
                  key={cle || 'tous'}
                  type="button"
                  aria-pressed={onglet === cle}
                  onClick={() => setOnglet(cle)}
                  className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition sm:px-4 [@media(pointer:coarse)]:min-h-[40px] ${
                    onglet === cle ? 'bg-codialis-navy text-white' : 'text-codialis-gray hover:bg-gray-100'
                  }`}
                >
                  {libelle} <span className={`ml-1 ${onglet === cle ? 'text-codialis-mint' : ''}`}>{compte(cle)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Grille des cours */}
          {donnees.cours.length === 0 ? (
            <div className="rounded-2xl bg-white p-6 text-center text-sm text-codialis-gray shadow-card">
              {filtre
                ? 'Aucun cours ne correspond à la recherche.'
                : ONGLETS.find((candidat) => candidat.cle === onglet)!.aucun}
              {filtre && (
                <button
                  type="button"
                  onClick={reinitialiser}
                  className="ml-2 font-semibold text-codialis-sky hover:underline"
                >
                  Réinitialiser
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {donnees.cours.map((cours) => (
                <CarteCours key={cours.id} cours={cours} avecAuteur={donnees.perimetre === 'TOUS'} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
